import { useEffect } from 'react';

import { useAppSyncEvents } from '@/shared/hooks/use-appsync-events/use-app-sync-events';
import { useInvalidateJobTags } from '@/shared/hooks/use-invalidate-job-tags/use-invalidate-job-tags';
import { TJobEventTypeEnum } from '@/shared/hooks/use-jobs-app-sync-events/use-jobs-app-sync-events.types';
import { auditApi } from '@/shared/redux/api/audit/audit-api';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import { setBackgroundMode } from '@/shared/redux/background-refetch/background-refetch.slice';
import { useAppDispatch, useAppSelector } from '@/shared/redux/hooks/hooks';
import { snakeToCamelCase } from '@/shared/utils/transform';

export const useJobsAppSyncEvents = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { data } = useReadTenantQuery({}, { skip: !user });
  const { invalidateJobTags } = useInvalidateJobTags();
  const { connectAndSubscribe, disconnect } = useAppSyncEvents({});

  const tenantId = data?.readTenant?.tenantId;
  const webSocketChannel = `default/${tenantId}/jobs`;

  const onMessageReceived = (data: any) => {
    const event = snakeToCamelCase(data?.event);
    const eventType = event?.eventType as TJobEventTypeEnum;

    if (!eventType) {
      console.warn('Invalid job event data', data);
      return;
    }

    dispatch(setBackgroundMode());

    void invalidateJobTags({
      eventType,
      payload: event,
    });

    dispatch(auditApi.util.invalidateTags([{ type: 'AuditLogs', id: 'LIST' }]));
  };

  // Keyed on the tenant id, not the query result: a tenant update invalidates
  // `Tenant`, and re-running on the refetched object would drop this channel.
  useEffect(() => {
    if (tenantId) {
      void connectAndSubscribe(webSocketChannel, onMessageReceived);
    }
    return disconnect;
  }, [tenantId]);
};
